/**
 * Cheap environment materials for themed dungeons:
 *  - poisonWaterMaterial: opaque two-layer panned water (dark bottle green, faint jade rim,
 *    never bloom-bright, nowhere near telegraph orange or venom lime); spout streams scroll down
 *  - lightPoolMaterial: additive fake light pools with per-vertex colour (warm braziers / cool jade)
 *  - shaftMaterial: soft additive god-ray cards (alpha ≤ 0.12)
 *  - floorDecalTexture: a procedural 2×2 atlas of moss / cracks / puddle / roots splats
 */
import * as THREE from 'three';
import { NOISE_GLSL, U } from './style';

export function poisonWaterMaterial(map: THREE.Texture | null): THREE.ShaderMaterial {
  return new THREE.ShaderMaterial({
    uniforms: {
      uMap: { value: map },
      uHasMap: { value: map ? 1 : 0 },
      uTime: U.uTime,
      uDeep: { value: new THREE.Color(0x173326) },
      uSurf: { value: new THREE.Color(0x2e6b4a) },
      uRim: { value: new THREE.Color(0x4fbf9a) },
      uFogColor: U.uFogColor,
      uFogNear: U.uFogNear,
      uFogFar: U.uFogFar,
    },
    vertexShader: `attribute float aEdge; attribute float aStream; varying vec3 vW; varying float vEdge; varying float vStream; varying vec2 vUv;
      void main(){ vEdge = aEdge; vStream = aStream; vUv = uv; vec4 w = modelMatrix * vec4(position, 1.0); vW = w.xyz; gl_Position = projectionMatrix * viewMatrix * w; }`,
    fragmentShader: `uniform sampler2D uMap; uniform float uHasMap; uniform float uTime; uniform vec3 uDeep; uniform vec3 uSurf; uniform vec3 uRim;
      uniform vec3 uFogColor; uniform float uFogNear; uniform float uFogFar;
      varying vec3 vW; varying float vEdge; varying float vStream; varying vec2 vUv;
      ${NOISE_GLSL}
      void main(){
        vec3 col;
        if (vStream > 0.5) {
          float n = noise(vec2(vUv.x * 6.0, vUv.y * 3.0 + uTime * 2.4));
          col = mix(uSurf * 0.9, uRim * 0.8, smoothstep(0.45, 0.9, n));
          float a = smoothstep(0.0, 0.25, vUv.x) * smoothstep(1.0, 0.75, vUv.x);
          if (a < 0.35) discard;
        } else {
          vec2 p = vW.xz / 6.0;
          vec3 t1 = uHasMap > 0.5 ? texture2D(uMap, p + vec2(uTime * 0.012, uTime * 0.007)).rgb : uSurf;
          vec3 t2 = uHasMap > 0.5 ? texture2D(uMap, p * 0.71 + vec2(-uTime * 0.009, uTime * 0.013)).rgb : uDeep;
          col = mix(t1, t2, 0.5) * 1.15;
          float sw = noise(p * 3.0 + vec2(uTime * 0.15, -uTime * 0.1));
          col = mix(col, uSurf, smoothstep(0.62, 0.9, sw) * 0.25);
          col = mix(col, uRim * 0.55, (1.0 - smoothstep(0.0, 1.0, vEdge)) * 0.35);
        }
        float d = length(vW - cameraPosition);
        col = mix(col, uFogColor, smoothstep(uFogNear, uFogFar, d) * 0.9);
        gl_FragColor = vec4(min(col, vec3(0.6)), 1.0);
      }`,
    side: THREE.DoubleSide,
  });
}

/** Additive light pools: aGlow = (r, g, b, strength), aSeed flickers each one differently. */
export function lightPoolMaterial(): THREE.ShaderMaterial {
  return new THREE.ShaderMaterial({
    uniforms: { uTime: U.uTime, uScale: { value: 0.7 } },
    vertexShader: `attribute vec4 aGlow; attribute float aSeed; varying vec2 vUv; varying vec4 vGlow; varying float vSeed;
      void main(){ vUv = uv; vGlow = aGlow; vSeed = aSeed; gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0); }`,
    fragmentShader: `uniform float uTime; uniform float uScale; varying vec2 vUv; varying vec4 vGlow; varying float vSeed;
      void main(){ float r = length(vUv * 2.0 - 1.0); if (r > 1.0) discard;
        float f = pow(1.0 - r, 2.2) * vGlow.a * uScale * (0.92 + 0.08 * sin(uTime * 7.0 + vSeed) * sin(uTime * 3.1 + vSeed * 2.0));
        gl_FragColor = vec4(vGlow.rgb * f, f); }`,
    transparent: true,
    depthWrite: false,
    blending: THREE.AdditiveBlending,
    polygonOffset: true,
    polygonOffsetFactor: -1,
    polygonOffsetUnits: -1,
  });
}

export function shaftMaterial(): THREE.ShaderMaterial {
  return new THREE.ShaderMaterial({
    uniforms: { uTime: U.uTime, uColor: { value: new THREE.Vector3(1.0, 0.93, 0.78) }, uAlpha: { value: 0.1 } },
    vertexShader: `varying vec2 vUv; varying vec3 vW; void main(){ vUv = uv; vec4 w = modelMatrix * vec4(position, 1.0); vW = w.xyz; gl_Position = projectionMatrix * viewMatrix * w; }`,
    fragmentShader: `uniform float uTime; uniform vec3 uColor; uniform float uAlpha; varying vec2 vUv; varying vec3 vW;
      ${NOISE_GLSL}
      void main(){
        float edge = smoothstep(0.0, 0.35, vUv.x) * smoothstep(1.0, 0.65, vUv.x);
        float vfade = smoothstep(0.0, 0.18, vUv.y) * smoothstep(1.0, 0.45, vUv.y);
        float n = 0.75 + 0.25 * noise(vec2(vUv.x * 4.0 + uTime * 0.05, vUv.y * 2.0 - uTime * 0.08));
        float a = edge * vfade * n * uAlpha;
        gl_FragColor = vec4(uColor * a, a);
      }`,
    transparent: true,
    depthWrite: false,
    blending: THREE.AdditiveBlending,
    side: THREE.DoubleSide,
  });
}

/** 2×2 decal atlas (cells: moss, cracks, puddle, roots) drawn on a canvas, RGBA. */
export function floorDecalTexture(): THREE.CanvasTexture | null {
  if (typeof document === 'undefined') return null;
  const S = 512;
  const H = S / 2;
  const cv = document.createElement('canvas');
  cv.width = cv.height = S;
  const g = cv.getContext('2d');
  if (!g) return null;
  let seed = 5;
  const rnd = (): number => (seed = (seed * 16807) % 2147483647) / 2147483647;
  const blob = (cx: number, cy: number, r: number, color: string, n: number): void => {
    for (let i = 0; i < n; i++) {
      const a = rnd() * Math.PI * 2;
      const d = Math.sqrt(rnd()) * r;
      const rr = (0.12 + rnd() * 0.22) * r;
      const grd = g.createRadialGradient(cx + Math.cos(a) * d, cy + Math.sin(a) * d, 0, cx + Math.cos(a) * d, cy + Math.sin(a) * d, rr);
      grd.addColorStop(0, color);
      grd.addColorStop(1, 'rgba(0,0,0,0)');
      g.fillStyle = grd;
      g.beginPath();
      g.arc(cx + Math.cos(a) * d, cy + Math.sin(a) * d, rr, 0, Math.PI * 2);
      g.fill();
    }
  };
  // Moss (muted olive, darker than the stone so it never reads as venom lime).
  g.save();
  g.beginPath();
  g.rect(0, 0, H, H);
  g.clip();
  blob(H / 2, H / 2, H * 0.36, 'rgba(70,82,44,0.55)', 40);
  blob(H / 2, H / 2, H * 0.3, 'rgba(52,62,34,0.45)', 24);
  g.restore();
  // Cracks.
  g.save();
  g.beginPath();
  g.rect(H, 0, H, H);
  g.clip();
  g.strokeStyle = 'rgba(16,14,12,0.85)';
  g.lineCap = 'round';
  for (let k = 0; k < 5; k++) {
    let x = H + H / 2;
    let y = H / 2;
    let a = rnd() * Math.PI * 2;
    g.lineWidth = 3.2;
    g.beginPath();
    g.moveTo(x, y);
    for (let i = 0; i < 9; i++) {
      a += (rnd() - 0.5) * 1.1;
      x += Math.cos(a) * 12;
      y += Math.sin(a) * 12;
      g.lineTo(x, y);
      g.lineWidth = Math.max(0.8, 3.2 - i * 0.3);
    }
    g.stroke();
  }
  g.restore();
  // Puddle (dark, cool, low contrast; a faint highlight).
  g.save();
  g.beginPath();
  g.rect(0, H, H, H);
  g.clip();
  blob(H / 2, H + H / 2, H * 0.32, 'rgba(18,30,28,0.6)', 30);
  g.strokeStyle = 'rgba(150,170,160,0.12)';
  g.lineWidth = 2;
  g.beginPath();
  g.ellipse(H / 2 - 10, H + H / 2 - 8, H * 0.18, H * 0.08, 0.3, 0.2, 2.2);
  g.stroke();
  g.restore();
  // Roots (dark brown tendrils).
  g.save();
  g.beginPath();
  g.rect(H, H, H, H);
  g.clip();
  g.strokeStyle = 'rgba(40,28,20,0.8)';
  g.lineCap = 'round';
  for (let k = 0; k < 6; k++) {
    let x = H + H / 2 + (rnd() - 0.5) * 40;
    let y = H + H / 2 + (rnd() - 0.5) * 40;
    let a = rnd() * Math.PI * 2;
    g.beginPath();
    g.moveTo(x, y);
    for (let i = 0; i < 12; i++) {
      a += (rnd() - 0.5) * 0.7;
      x += Math.cos(a) * 9;
      y += Math.sin(a) * 9;
      g.lineWidth = Math.max(1, 6 - i * 0.45);
      g.lineTo(x, y);
    }
    g.stroke();
  }
  g.restore();
  const tex = new THREE.CanvasTexture(cv);
  tex.colorSpace = THREE.SRGBColorSpace;
  return tex;
}

/** Atlas cell (u0, v0) for a decal kind index (canvas row 0 = top; flipY on). */
export const DECAL_CELL: readonly (readonly [number, number])[] = [
  [0, 0.5],
  [0.5, 0.5],
  [0, 0],
  [0.5, 0],
];
